import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { formatTimezoneLabel } from '@/lib/format/timezone';

const facebookHosts = new Set(['facebook.com', 'www.facebook.com', 'm.facebook.com', 'web.facebook.com']);
const reservedProfilePaths = new Set([
  'about', 'ad_center', 'ads', 'adsmanager', 'ajax', 'api', 'appcenter', 'apps',
  'business', 'businesses', 'checkpoint', 'connect', 'developers', 'dialog',
  'events', 'friends', 'fundraiser', 'fundraisers', 'gaming', 'groups', 'help',
  'home', 'login', 'logout', 'marketplace', 'messages', 'notifications', 'oauth',
  'pages', 'people', 'photo', 'photos', 'plugins', 'policies', 'post', 'posts',
  'privacy', 'recover', 'redirect', 'reel', 'reels', 'saved', 'search', 'settings',
  'share', 'sharer', 'stories', 'story', 'terms', 'video', 'videos', 'watch',
]);

function safeProfileUrl(value: unknown): string | null {
  // Check the original authority too: URL normalizes an explicit :443 away.
  if (typeof value !== 'string' || value.length > 2048 || !/^https:\/\/(?:www\.|m\.|web\.)?facebook\.com(?:[/?#]|$)/i.test(value)) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || !facebookHosts.has(url.hostname) || url.username || url.password || url.port) return null;
    const path = url.pathname.replace(/\/$/, '');
    if (path === '/profile.php') {
      const ids = url.searchParams.getAll('id');
      return ids.length === 1 && /^\d+$/.test(ids[0]) ? `${url.origin}/profile.php?id=${ids[0]}` : null;
    }
    const named = path.match(/^\/([A-Za-z0-9._-]+)$/);
    const identified = path.match(/^\/(?:people|pages)\/([A-Za-z0-9._-]+)\/\d+$/);
    const slug = named?.[1] ?? identified?.[1];
    if (!slug || slug.toLowerCase().includes('.php') || (named && reservedProfilePaths.has(slug.toLowerCase()))) return null;
    return `${url.origin}${path}`;
  } catch { return null; }
}

function formatInstant(value: unknown, locale: string | undefined, timezone: string): string | null {
  // Date-only metadata does not establish a publication instant.
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/.test(value) || !Number.isFinite(Date.parse(value))) return null;
  return `${new Date(value).toLocaleString(locale, { timeZone: timezone })} (${formatTimezoneLabel(timezone)})`;
}

export function FacebookFirstPassDiagnostics({ firstPass, timezone }: {
  firstPass: Record<string, unknown>;
  timezone: string;
}) {
  const { t, i18n } = useTranslation();
  if (firstPass.status !== 'checked' && firstPass.status !== 'unavailable') return null;

  const locale = i18n?.resolvedLanguage || i18n?.language;
  const provider = firstPass.provider === 'yt-dlp' ? 'yt-dlp' : firstPass.provider === 'sociavault' ? 'SociaVault' : null;
  const views = typeof firstPass.observed_views === 'string' && /^\d{1,100}$/.test(firstPass.observed_views)
    ? BigInt(firstPass.observed_views).toLocaleString(locale) : null;
  const publishedAt = formatInstant(firstPass.published_at, locale, timezone);
  const checkedAt = formatInstant(firstPass.checked_at, locale, timezone);
  const profileUrl = safeProfileUrl(firstPass.profile_url);
  const unknown = t('ranking.diagnostics.facebook.unknown');

  return <section className="min-w-0 space-y-2 text-foreground" aria-label={t('ranking.diagnostics.facebook.title')}>
    <p className="font-medium">{t('ranking.diagnostics.facebook.title')}</p>
    <div className="flex flex-wrap gap-2">
      <Badge variant="outline">{t('ranking.diagnostics.facebook.unverified')}</Badge>
      <Badge variant="outline">{t(`ranking.diagnostics.facebook.${firstPass.status}`)}</Badge>
    </div>
    {firstPass.status === 'unavailable' && <p className="text-xs text-muted-foreground">{t('ranking.diagnostics.facebook.unavailableHelp')}</p>}
    <dl className="space-y-1">
      {provider && <div className="flex flex-wrap gap-x-2"><dt className="text-muted-foreground">{t('ranking.diagnostics.facebook.provider')}:</dt><dd>{provider}</dd></div>}
      <div className="flex flex-wrap gap-x-2"><dt className="text-muted-foreground">{t('ranking.diagnostics.facebook.observedViews')}:</dt><dd className="break-all tabular-nums">{views ?? unknown}</dd></div>
      <div className="flex flex-wrap gap-x-2"><dt className="text-muted-foreground">{t('ranking.diagnostics.facebook.publishedAt')}:</dt><dd>{publishedAt ?? unknown}</dd></div>
    </dl>
    {profileUrl && <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-primary underline">{t('ranking.diagnostics.facebook.openProfile')}</a>}
    {checkedAt && <p className="text-xs text-muted-foreground">{t('ranking.diagnostics.facebook.checkedAt', { time: checkedAt })}</p>}
    <p className="text-xs text-muted-foreground">{t('ranking.diagnostics.facebook.manualReviewRequired')}</p>
  </section>;
}
